<div class="ns-page" [class.is-view-only]="!canUpdate">
  <header
    class="ns-hero"
    *ngIf="!loading && !isOnDemand"
    [class.is-paused]="!loadError && serviceState === 'paused'"
    [class.is-idle]="!loadError && serviceState === 'live-none'"
    [class.is-live]="!loadError && serviceState === 'live'"
  >
    <div class="ns-hero__copy">
      <p class="ns-kicker">Project settings</p>
      <h1 class="ns-title">Notification Setup</h1>
      <p class="ns-lead">
        Build the invitation sequence one attempt at a time. The first attempt is the invitation;
        later attempts are reminders. Providers are shared; each attempt has its own timing, channels, and messages.
      </p>
    </div>
    <aside class="ns-status" *ngIf="!loading && !loadError">
      <div class="ns-status__cell">
        <span class="ns-status__kicker">Notification service</span>
        <div class="ns-status__value">
          <span class="ns-status__dot" aria-hidden="true"></span>
          <strong>{{ config.notifications_enabled ? 'Live' : 'Paused' }}</strong>
        </div>
        <div class="ns-live" role="group" aria-label="Notification service">
          <button
            type="button"
            [class.is-on]="config.notifications_enabled"
            [class.is-unauthorized]="!canUpdate"
            [disabled]="canUpdate && headerUpdating"
            (click)="setServiceLive(true)"
          >
            Live
          </button>
          <button
            type="button"
            [class.is-on]="!config.notifications_enabled"
            [class.is-unauthorized]="!canUpdate"
            [disabled]="canUpdate && headerUpdating"
            (click)="setServiceLive(false)"
          >
            Pause
          </button>
        </div>
        <p class="ns-status__hint" *ngIf="savingService">Updating…</p>
        <p class="ns-status__hint" *ngIf="!savingService && config.notifications_enabled">
          <strong>Live</strong> sends invitations and reminders, while <strong>Pause</strong> stops them until the service is turned on again.
        </p>
        <p class="ns-status__hint" *ngIf="!savingService && !config.notifications_enabled">
          <strong>Pause</strong> stops invitations and reminders. <strong>Live</strong> resumes them and picks up the last {{ catchUpDays }} days of requests.
        </p>
      </div>
      <div class="ns-status__cell">
        <span class="ns-status__kicker">Delivery channels</span>
        <div class="ns-status__value">
          <ng-container *ngIf="hasDeliveryChannel && deliveryLabels.length; else deliveryNone">
            <span class="ns-status__chip" *ngFor="let name of deliveryLabels">{{ name }}</span>
          </ng-container>
          <ng-template #deliveryNone>
            <strong>None</strong>
          </ng-template>
        </div>
        <div class="ns-live ns-live--idle" role="group" aria-label="Delivery channels">
          <button
            type="button"
            [class.is-on]="hasDeliveryChannel"
            [class.is-unauthorized]="!canUpdate"
            [disabled]="canUpdate && headerUpdating"
            (click)="setDeliveryEnabled(true)"
          >
            Send
          </button>
          <button
            type="button"
            [class.is-on]="!hasDeliveryChannel"
            [class.is-unauthorized]="!canUpdate"
            [disabled]="canUpdate && headerUpdating"
            (click)="setDeliveryEnabled(false)"
          >
            None
          </button>
        </div>
        <p class="ns-status__hint" *ngIf="savingDelivery">Updating…</p>
        <p class="ns-status__hint" *ngIf="!savingDelivery">
          <strong>Send</strong> sends the first invitation and reminders via SMS or Email, while <strong>None</strong> processes the records without sending any messages.
        </p>
      </div>
    </aside>
  </header>

  <header class="ns-hero ns-hero--on-demand" *ngIf="!loading && isOnDemand">
    <div class="ns-hero__copy">
      <p class="ns-kicker">Project settings</p>
      <h1 class="ns-title">Backup Attempt</h1>
      <p class="ns-lead">
        Configure backup notification attempts for requests you trigger manually from the dashboard.
        Pick SMS, email, and WhatsApp templates from this project's template list.
      </p>
    </div>
    <aside class="ns-status ns-status--single" *ngIf="!loadError">
      <div class="ns-status__cell">
        <span class="ns-status__kicker">Delivery channels</span>
        <div class="ns-status__value">
          <ng-container *ngIf="backupDeliveryLabels.length; else backupDeliveryNone">
            <span class="ns-status__chip" *ngFor="let name of backupDeliveryLabels">{{ name }}</span>
          </ng-container>
          <ng-template #backupDeliveryNone>
            <strong>None</strong>
          </ng-template>
        </div>
        <p class="ns-status__hint">
          Channels used on backup attempts.
        </p>
      </div>
    </aside>
  </header>

  <div class="ns-banner ns-banner--warn" *ngIf="!conversationReady && !loading">
    Set up the conversation and publish it before you set up channels.
    Publishing creates the feedback link this flow needs.
    <a class="ns-text-link" routerLink="/account/project/settings/interaction" queryParamsHandling="preserve">Open Conversation setup</a>
  </div>

  <div class="ns-banner ns-banner--error" *ngIf="!loading && !loadError && sequenceWindowNotice.length">
    <p>
      <ng-container *ngFor="let part of sequenceWindowNotice">
        <strong *ngIf="part.strong">{{ part.text }}</strong>
        <ng-container *ngIf="!part.strong">{{ part.text }}</ng-container>
      </ng-container>
      <button type="button" class="ns-link-btn ns-link-btn--inline" (click)="openFiltersAndTiming($event)">Filters &amp; timing</button>.
    </p>
  </div>

  <div class="ns-banner ns-banner--warn" *ngIf="!loading && !loadError && isOverAttemptLimit">
    {{ attemptOverLimitMessage }}
  </div>

  <div class="ns-banner ns-banner--error" *ngIf="loadError">
    {{ loadError }}
    <button type="button" class="ns-link-btn" (click)="load()">Retry</button>
  </div>

  <div class="ns-loading" *ngIf="loading">
    <app-ltms-loader></app-ltms-loader>
  </div>

  <section class="ns-landing" *ngIf="!loading && !loadError && !config.attempts.length">
    <div class="ns-landing__copy">
      <p class="ns-kicker">Not configured yet</p>
      <h2>{{ isOnDemand ? 'Set up backup attempts' : 'Set up how customers are invited' }}</h2>
      <p *ngIf="!isOnDemand">
        No invitation attempts are configured for this project. Create a sequence: when to send,
        which channels to use, and the message for each one.
      </p>
      <p *ngIf="isOnDemand">
        No backup attempts are configured. Add a sequence that uses SMS, email, and WhatsApp templates from the Templates tab. You can then trigger a request manually from the dashboard.
      </p>
    </div>
    <div class="ns-landing__cards">
      <article>
        <span>1</span>
        <strong>When to send</strong>
        <p>Send immediately, after a delay, or at a specific date and time.</p>
      </article>
      <article>
        <span>2</span>
        <strong>Choose channels</strong>
        <p>Use SMS, Email, and WhatsApp on each attempt. Providers are shared.</p>
      </article>
      <article>
        <span>3</span>
        <strong>Write messages</strong>
        <p>Add the text or HTML template, including merge fields like customer name and feedback link.</p>
      </article>
    </div>
    <button type="button" class="ns-save" (click)="startSetup()" [class.is-unauthorized]="!canUpdate">
      Get started
    </button>
  </section>

  <div class="ns-layout" *ngIf="!loading && !loadError && config.attempts.length">
    <aside class="ns-sidebar">
      <div class="ns-sidebar__head">
        <h2>{{ isOnDemand ? 'Backup attempt sequence' : 'Attempt sequence' }}</h2>
        <span class="ns-count">{{ config.attempts.length }} total</span>
      </div>

      <div class="ns-seq-list" #attemptList>
        <button
          type="button"
          class="ns-seq"
          *ngFor="let attempt of config.attempts; let i = index"
          [class.is-active]="selectedAttemptIndex === i"
          [class.is-draft]="!isAttemptSaved(i)"
          [class.is-changed]="isAttemptChanged(i)"
          [class.is-late]="attemptDelayMissesWindow(i)"
          (click)="selectAttempt(i)"
        >
        <span class="ns-seq__top">
          <strong>{{ attemptDisplayName(i) }}</strong>
          <span
            class="ns-seq__status"
            [class.is-saved]="isAttemptSaved(i) && !isAttemptChanged(i) && !attemptDelayMissesWindow(i)"
            [class.is-changed]="isAttemptChanged(i) && !attemptDelayMissesWindow(i)"
            [class.is-late]="attemptDelayMissesWindow(i)"
          >
            {{ attemptStatusLabel(i) }}
          </span>
        </span>
        <span class="ns-seq__detail">
          <ng-container *ngFor="let part of countDelayFromParts(attempt, i)">
            <strong *ngIf="part.strong">{{ part.text }}</strong>
            <ng-container *ngIf="!part.strong">{{ part.text }}</ng-container>
          </ng-container>
        </span>
        <span class="ns-seq__chips">
          <span
            class="ns-seq__channel"
            *ngFor="let chip of attemptChannelLangChips(attempt)"
            [attr.data-channel]="chip.key"
            [title]="chip.title"
          >
            <span class="ns-seq__channel-mark" aria-hidden="true">
              <i class="fa" [ngClass]="channelIcon(chip.key)"></i>
            </span>
            <span class="ns-seq__channel-copy">
              <span class="ns-seq__channel-name">{{ chip.name }}</span>
              <span class="ns-seq__langs" [attr.data-lang-row]="i + ':' + chip.key">
                <em *ngFor="let lang of fittedLangs(i, chip)" [class.is-default]="lang.isDefault" [title]="lang.isDefault ? lang.title + ' is the default language' : lang.title">
                  <i class="fa fa-star" *ngIf="lang.isDefault" aria-hidden="true"></i>{{ lang.code }}
                </em>
                <em class="is-more" *ngIf="fittedExtra(i, chip) as extra" [title]="fittedExtraTitle(i, chip)">+{{ extra }}</em>
              </span>
            </span>
          </span>
          <span class="ns-seq__chip is-empty" *ngIf="!attemptChannelLangChips(attempt).length">No channels</span>
        </span>
        </button>
      </div>

      <button
        type="button"
        class="ns-add-attempt"
        (click)="addAttempt()"
        [title]="addAttemptHint"
        [class.is-muted]="canUpdate && !canAddAttempt"
        [class.is-unauthorized]="!canUpdate"
      >
        + Add attempt
      </button>

      <div class="ns-shared">
        <p>SMS, Email, and WhatsApp providers are shared. Changing sender or gateway on one attempt updates every attempt.</p>
      </div>
    </aside>

    <section class="ns-main" #attemptMain *ngIf="selectedAttempt">
      <div class="ns-main__head" [id]="attemptHeadId">
        <div>
          <p class="ns-kicker">{{ attemptPad(selectedAttemptIndex) }}</p>
          <h2>{{ attemptDisplayName(selectedAttemptIndex) }}</h2>
        </div>
        <div class="ns-main__links">
          <button
            type="button"
            (click)="duplicateAttempt(selectedAttemptIndex)"
            [class.is-muted]="canUpdate && !canDuplicateSelectedAttempt && !hasExistingDuplicate(selectedAttemptIndex)"
            [class.is-unauthorized]="!canUpdate"
            [title]="duplicateAttemptHint"
          >
            Duplicate
          </button>
          <button
            type="button"
            class="ns-icon-btn ns-icon-btn--danger"
            (click)="removeAttempt(selectedAttemptIndex)"
            [class.is-muted]="canUpdate && !canRemoveSelectedAttempt"
            [class.is-unauthorized]="!canUpdate"
            [attr.aria-label]="removeAttemptHint"
            [title]="removeAttemptHint"
          >
            <i class="fa fa-trash" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <article class="ns-step">
        <h3><span>1</span> When should this attempt send?</h3>
        <ng-container *ngIf="!isOnDemand">
        <p class="ns-step__label">Count delay from</p>
        <p class="ns-help">The delay starts from this event. Last attempt date is only for reminders.</p>
        <div class="ns-tiles" role="group" aria-label="Count delay from">
          <button
            type="button"
            class="ns-tile"
            [class.is-on]="selectedAttempt.based_on === 1"
            [class.is-unauthorized]="!canUpdate"
            (click)="setAttemptBasedOn(1)"
          >
            <strong>{{ selectedAttemptIndex === 0 ? 'Request Received' : 'Invitation date' }}</strong>
            <span>When this conversation was requested. The delay starts from that request.</span>
          </button>
          <button
            type="button"
            class="ns-tile"
            [class.is-on]="selectedAttemptIndex > 0 && selectedAttempt.based_on === 2"
            [class.is-locked]="selectedAttemptIndex === 0"
            [class.is-unauthorized]="!canUpdate && selectedAttemptIndex > 0"
            [disabled]="selectedAttemptIndex === 0"
            (click)="setAttemptBasedOn(2)"
          >
            <strong>Last attempt date</strong>
            <span *ngIf="selectedAttemptIndex === 0">Only for reminders. The first attempt has no previous send.</span>
            <span *ngIf="selectedAttemptIndex > 0">When the previous attempt was sent. The delay starts from that send.</span>
          </button>
        </div>
        <p class="ns-help">{{ requestTriggerHint }}</p>
        <p class="ns-step__label">How it waits</p>
        <div class="ns-tiles">
          <button type="button" class="ns-tile" [class.is-on]="selectedAttempt.timing !== 'absolute'" [class.is-unauthorized]="!canUpdate" (click)="setAttemptTiming('relative')">
            <strong>Relative delay</strong>
            <span>Send after a configured delay.</span>
          </button>
          <button type="button" class="ns-tile" [class.is-on]="selectedAttempt.timing === 'absolute'" [class.is-unauthorized]="!canUpdate" (click)="setAttemptTiming('absolute')">
            <strong>Specific date &amp; time</strong>
            <span>Choose a fixed send time.</span>
          </button>
        </div>
        </ng-container>
        <label *ngIf="isOnDemand || selectedAttempt.timing !== 'absolute'" [class.is-invalid]="delayMinutesGap(selectedAttempt)" [class.is-dirty]="isDelayFieldDirty() && !delayMinutesGap(selectedAttempt)">
          Delay in minutes
          <input
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            autocomplete="off"
            [attr.maxlength]="delayMinutesCharMax"
            [ngModel]="selectedAttempt.delay_minutes"
            [disabled]="!canUpdate"
            (ngModelChange)="setDelayMinutes($event)"
            (input)="onDelayMinutesInput($event)"
            (keydown)="blockNonIntegerKeys($event)"
          />
          <small>{{ delayHumanMessage(selectedAttempt.delay_minutes, selectedAttemptIndex) }}</small>
          <small class="ns-field-error" *ngIf="delayWindowRows(selectedAttempt).length">
            <span *ngFor="let message of delayWindowRows(selectedAttempt)">
              {{ message }}
              <button type="button" class="ns-link-btn ns-link-btn--inline" (click)="openFiltersAndTiming($event)">Filters &amp; timing</button>.
            </span>
          </small>
          <small class="ns-field-error" *ngIf="!delayWindowRows(selectedAttempt).length && delayMinutesGap(selectedAttempt)">{{ delayMinutesGap(selectedAttempt) }}</small>
        </label>
        <label *ngIf="!isOnDemand && selectedAttempt.timing === 'absolute'" [class.is-dirty]="isAbsoluteAtDirty()">
          Send at
          <input
            type="datetime-local"
            [min]="absoluteAtMin"
            [max]="absoluteAtMax"
            [ngModel]="selectedAttempt.absolute_at"
            [disabled]="!canUpdate"
            (ngModelChange)="setAbsoluteAt($event)"
          />
          <small>Pick a future time within {{ delayMaxDays }} days for {{ attemptDisplayName(selectedAttemptIndex) }}.</small>
        </label>
      </article>

      <article class="ns-step">
        <h3><span>2</span> Choose delivery channels</h3>
        <p class="ns-help">Set up a channel to include it on this attempt. Use the edit or delete icons on a selected channel. Keep at least one.</p>
        <div class="ns-channel-cards">
          <div
            class="ns-channel-card"
            *ngFor="let key of orderedDeliveryChannels"
            [class.is-on]="isChannelSelected(key)"
            [class.is-editing]="channelEditor === key"
            [attr.data-channel]="key"
          >
            <span class="ns-channel-card__icon" aria-hidden="true">
              <i class="fa" [ngClass]="channelIcon(key)"></i>
            </span>
            <span class="ns-channel-card__copy">
              <strong>{{ channelLabel(key) }}</strong>
              <em>{{ isChannelSelected(key) ? 'On this attempt' : 'Not on this attempt' }}</em>
            </span>
            <div class="ns-channel-card__actions">
              <button
                type="button"
                class="ns-icon-btn"
                *ngIf="isChannelSelected(key)"
                [attr.aria-label]="canUpdate ? 'Edit channel' : 'View channel'"
                [title]="canUpdate ? 'Edit' : 'View'"
                (click)="openChannelEditor(key)"
              >
                <i class="fa" [class.fa-pencil]="canUpdate" [class.fa-eye]="!canUpdate" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="ns-icon-btn ns-icon-btn--danger"
                *ngIf="isChannelSelected(key)"
                [class.is-unauthorized]="!canUpdate"
                [disabled]="canUpdate && !canRemoveAttemptChannel(key)"
                [attr.aria-label]="removeAttemptChannelHint(key)"
                [title]="removeAttemptChannelHint(key)"
                (click)="removeAttemptChannel(key)"
              >
                <i class="fa fa-trash" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="ns-setup-btn"
                *ngIf="!isChannelSelected(key)"
                [class.is-unauthorized]="!canUpdate"
                (click)="setupChannel(key)"
              >
                Set up
              </button>
            </div>
          </div>
        </div>

        <div class="ns-provider-stack" *ngIf="selectedDeliveryChannels.length">
          <div class="ns-provider-block ns-readonly" *ngFor="let key of selectedDeliveryChannels" [attr.data-channel]="key">
            <div class="ns-provider-block__head">
              <div class="ns-provider-block__title">
                <span class="ns-block-icon" aria-hidden="true">
                  <i class="fa" [ngClass]="channelIcon(key)"></i>
                </span>
                <div class="ns-provider-block__copy">
                  <div class="ns-provider-block__name">
                    <h4>{{ channelLabel(key) }} Provider</h4>
                    <span class="ns-shared-badge">Shared</span>
                  </div>
                  <small>Across languages and attempts</small>
                </div>
              </div>
              <div class="ns-msg-block__tools">
                <button type="button" class="ns-setup-btn" (click)="openChannelEditor(key, 'provider')">{{ canUpdate ? 'Edit' : 'View' }}</button>
              </div>
            </div>

            <div class="ns-identity ns-identity--review" *ngIf="key === 'sms'">
              <label>
                Sender ID
                <span class="ns-fit">{{ displayValue(config.channels.sms.sender_id) }}</span>
              </label>
              <label>
                SMS Provider (India)
                <span class="ns-fit" [title]="providerName(providers.sms.domestic, config.channels.sms.provider)">
                  {{ providerName(providers.sms.domestic, config.channels.sms.provider) }}
                </span>
              </label>
              <label>
                SMS International Provider
                <span class="ns-fit" [title]="providerName(providers.sms.international, config.channels.sms.international_provider)">
                  {{ providerName(providers.sms.international, config.channels.sms.international_provider) }}
                </span>
              </label>
              <p class="ns-warn" *ngIf="providerUnavailable(findProvider(providers.sms.domestic, config.channels.sms.provider))">
                This India SMS provider is marked unavailable.
              </p>
              <div class="ns-sms-trim">
                <label>
                  SMS Trim
                  <span class="ns-fit ns-fit--check">
                    <input type="checkbox" [ngModel]="config.channels.sms.sms_trim_enabled" disabled />
                    {{ config.channels.sms.sms_trim_enabled ? 'On' : 'Off' }}
                  </span>
                </label>
                <label [class.is-muted]="!config.channels.sms.sms_trim_enabled">
                  Recipient name max. length
                  <span class="ns-fit">{{ displayValue(config.channels.sms.recipient_name_length) }}</span>
                </label>
                <label [class.is-muted]="!config.channels.sms.sms_trim_enabled">
                  Default recipient name
                  <span class="ns-fit">{{ displayValue(config.channels.sms.default_recipient_name) }}</span>
                </label>
              </div>
            </div>

            <div class="ns-identity ns-identity--review ns-identity--email" *ngIf="key === 'email'">
              <label class="ns-identity__wide">
                From address
                <span class="ns-fit ns-fit--from">{{ displayValue(config.channels.email.from) }}</span>
              </label>
              <label>
                Email Provider
                <span class="ns-fit" [title]="providerName(providers.email, config.channels.email.provider)">
                  {{ providerName(providers.email, config.channels.email.provider) }}
                </span>
              </label>
              <label>
                Reply-to (optional)
                <span class="ns-fit" [title]="displayValue(config.channels.email.reply_to)">
                  {{ displayValue(config.channels.email.reply_to) }}
                </span>
              </label>
              <p class="ns-warn" *ngIf="providerUnavailable(findProvider(providers.email, config.channels.email.provider))">
                This email provider is marked unavailable.
              </p>
            </div>

            <div class="ns-identity ns-identity--review" *ngIf="key === 'whatsapp'">
              <label>
                Sender ID
                <span class="ns-fit">{{ displayValue(config.channels.whatsapp.sender_id) }}</span>
              </label>
              <label>
                WhatsApp Provider (India)
                <span class="ns-fit" [title]="providerName(providers.whatsapp.domestic, config.channels.whatsapp.domestic_provider)">
                  {{ providerName(providers.whatsapp.domestic, config.channels.whatsapp.domestic_provider) }}
                </span>
              </label>
              <label>
                WhatsApp International Provider
                <span class="ns-fit" [title]="providerName(providers.whatsapp.international, config.channels.whatsapp.international_provider)">
                  {{ providerName(providers.whatsapp.international, config.channels.whatsapp.international_provider) }}
                </span>
              </label>
              <p class="ns-warn" *ngIf="providerUnavailable(findProvider(providers.whatsapp.domestic, config.channels.whatsapp.domestic_provider))">
                This India WhatsApp provider is marked unavailable.
              </p>
              <p class="ns-warn" *ngIf="providerUnavailable(findProvider(providers.whatsapp.international, config.channels.whatsapp.international_provider))">
                This international WhatsApp provider is marked unavailable.
              </p>
            </div>
          </div>
        </div>
      </article>

      <article class="ns-step">
        <h3><span>3</span> Configure messages</h3>
        <p class="ns-help" *ngIf="!attemptMessageTabs.length">Select at least one channel in step 2 to write messages.</p>

        <div class="ns-provider-stack ns-msg-stack" *ngIf="attemptMessageTabs.length">
          <div class="ns-provider-block ns-readonly" *ngFor="let key of attemptMessageTabs" [attr.data-channel]="key">
            <div class="ns-provider-block__head ns-msg-head">
              <div class="ns-provider-block__title">
                <span class="ns-block-icon" aria-hidden="true">
                  <i class="fa" [ngClass]="channelIcon(key)"></i>
                </span>
                <div class="ns-provider-block__copy">
                  <div class="ns-provider-block__name">
                    <h4>{{ channelLabel(key) }}</h4>
                    <span class="ns-shared-badge">This attempt</span>
                  </div>
                  <small>Copy for this attempt and language</small>
                </div>
              </div>
              <button type="button" class="ns-setup-btn" (click)="openChannelEditor(key, 'messages')">{{ canUpdate ? 'Edit' : 'View' }}</button>
            </div>
            <div class="ns-msg-langs">
              <button
                type="button"
                class="ns-lang ns-lang--code"
                *ngFor="let lang of languagesFor(key)"
                [class.is-active]="reviewLanguage(key) === lang"
                [class.is-default]="isDefaultLanguage(lang, key)"
                [title]="languageLabel(lang)"
                (click)="setReviewLanguage(key, lang)"
              >
                {{ languageShort(lang) }}
                <em *ngIf="isDefaultLanguage(lang, key)">Default</em>
              </button>
            </div>
            <p class="ns-help" *ngIf="!languagesFor(key).length">Choose a language, then add the template details.</p>

            <div class="ns-identity ns-identity--review" *ngIf="key === 'sms' && languagesFor(key).length">
              <label class="ns-identity__wide">
                {{ isOnDemand ? 'SMS template' : 'Content Template Id' }}
                <span class="ns-fit" [class.is-empty]="!templateFor(key).content_template_id?.trim()">
                  {{ displayValue(templateFor(key).content_template_id) }}
                </span>
              </label>
              <label class="ns-identity__wide">
                {{ isOnDemand ? 'SMS template text' : 'Content Template Id Text' }}
                <span class="ns-fit ns-fit--body" [class.is-empty]="!smsTemplateText(templateFor(key)).trim()">
                  {{ displayValue(smsTemplateText(templateFor(key))) }}
                </span>
              </label>
              <label class="ns-identity__wide" *ngIf="!isOnDemand">
                Message
                <span class="ns-fit ns-fit--body" [class.is-empty]="!templateFor(key).sms_body.trim()">
                  {{ displayValue(templateFor(key).sms_body) }}
                </span>
              </label>
              <p class="ns-warn" *ngIf="!isOnDemand && !templateFor(key).sms_body.trim()">
                Write the SMS message for this language before you can save this attempt.
              </p>
              <p class="ns-warn" *ngIf="isOnDemand && !templateFor(key).content_template_id?.trim()">
                Select an SMS template for this language before you can save this attempt.
              </p>
              <div class="ns-identity ns-identity--review ns-identity--sms-stats" *ngIf="smsReviewStats(key) as smsStats">
                <label>
                  Encoding
                  <span class="ns-fit">{{ smsStats.encodingLabel }}</span>
                </label>
                <label>
                  SMS parts
                  <span class="ns-fit">{{ smsStats.parts }}</span>
                </label>
                <label>
                  Chars used
                  <span class="ns-fit" [class.is-over]="smsStats.over">{{ smsStats.charsUsed }}</span>
                </label>
                <label>
                  Chars in SMS
                  <span class="ns-fit">{{ smsStats.charsInSms }}</span>
                </label>
              </div>
            </div>

            <div class="ns-identity ns-identity--review ns-identity--email" *ngIf="key === 'email' && languagesFor(key).length">
              <label class="ns-identity__wide">
                Subject
                <span class="ns-fit" [class.is-empty]="!templateFor(key).email_subject.trim()">
                  {{ displayValue(templateFor(key).email_subject) }}
                </span>
              </label>
              <p class="ns-warn" *ngIf="!isOnDemand && !templateFor(key).email_subject.trim()">
                Email subject is required before you can save this attempt.
              </p>
              <p class="ns-warn" *ngIf="!isOnDemand && !hasEmailTemplateFor(templateFor(key))">
                Upload an HTML template for this language before you can save this attempt.
              </p>
              <p class="ns-warn" *ngIf="isOnDemand && !hasEmailTemplateFor(templateFor(key))">
                Select an email template for this language before you can save this attempt.
              </p>
              <label class="ns-identity__wide">
                {{ isOnDemand ? 'Email template' : 'HTML template' }}
                <span class="ns-fit ns-fit--with-actions" [class.is-empty]="!emailHtmlLabelFor(templateFor(key))">
                  <span>{{ emailHtmlLabelFor(templateFor(key)) || (isOnDemand ? 'No template selected' : 'No HTML template yet') }}</span>
                  <span class="ns-fit__actions">
                    <button
                      type="button"
                      class="ns-save ns-save--sm ns-save--ghost"
                      (click)="reviewEmailTemplate(key, 'view')"
                      [disabled]="!hasEmailTemplateFor(templateFor(key))"
                    >
                      View
                    </button>
                    <button
                      type="button"
                      class="ns-save ns-save--sm ns-save--ghost"
                      (click)="reviewEmailTemplate(key, 'preview')"
                      [disabled]="!hasEmailTemplateFor(templateFor(key))"
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      class="ns-save ns-save--sm ns-save--ghost"
                      (click)="reviewEmailTemplate(key, 'download')"
                      [disabled]="!hasEmailTemplateFor(templateFor(key))"
                    >
                      Download
                    </button>
                  </span>
                </span>
              </label>
            </div>

            <div class="ns-identity ns-identity--review" *ngIf="key === 'whatsapp' && languagesFor(key).length">
              <label class="ns-identity__wide">
                WhatsApp Template Id
                <span class="ns-fit" [class.is-empty]="!templateFor(key).whatsapp_template_id?.trim()">
                  {{ displayValue(templateFor(key).whatsapp_template_id) }}
                </span>
              </label>
              <label class="ns-identity__wide">
                WhatsApp Template Id Text
                <span class="ns-fit ns-fit--body" [class.is-empty]="!templateFor(key).whatsapp_body.trim()">
                  {{ displayValue(templateFor(key).whatsapp_body) }}
                </span>
              </label>
              <p class="ns-warn" *ngIf="!templateFor(key).whatsapp_template_id?.trim()">
                Select a WhatsApp Template Id for this language before you can save this attempt.
              </p>
              <p class="ns-warn" *ngIf="!isOnDemand && templateFor(key).whatsapp_template_id?.trim() && !templateFor(key).whatsapp_body.trim()">
                Select a WhatsApp template that has template text before you can save this attempt.
              </p>
            </div>
          </div>
        </div>
      </article>

      <footer class="ns-main__foot">
        <div class="ns-main__foot-attempt is-hidden" [id]="attemptFootId" aria-hidden="true">
          <p class="ns-kicker">{{ attemptPad(selectedAttemptIndex) }}</p>
          <h2>{{ attemptDisplayName(selectedAttemptIndex) }}</h2>
        </div>
        <div class="ns-main__foot-actions">
          <button
            type="button"
            class="ns-save ns-save--ghost"
            [disabled]="!canDiscardAttempt"
            [title]="canDiscardAttempt ? 'Discard unsaved changes to ' + attemptDisplayName(selectedAttemptIndex) : ''"
            (click)="discardAttemptChanges()"
          >
            Discard changes
          </button>
          <button
            type="button"
            class="ns-save"
            [class.is-unauthorized]="!canUpdate"
            [disabled]="canUpdate && !canSaveAttempt"
            [title]="smsTemplateGap(selectedAttempt) || emailSubjectGap(selectedAttempt) || whatsappTemplateGap(selectedAttempt) || delayMinutesGap(selectedAttempt) || ''"
            (click)="save()"
          >
            {{ saving ? 'Saving…' : 'Save attempt' }}
          </button>
        </div>
      </footer>
    </section>
  </div>

  <div class="ns-drawer" *ngIf="channelEditor">
    <div class="ns-drawer__overlay" (click)="closeChannelEditor()"></div>
    <aside class="ns-drawer__panel" role="dialog" aria-modal="true" aria-labelledby="ns-channel-editor-title">
      <header class="ns-panel__head ns-panel__head--editor">
        <div class="ns-panel__identity">
          <p class="ns-kicker">{{ attemptPad(selectedAttemptIndex) }}</p>
          <h2 id="ns-channel-editor-title">{{ attemptDisplayName(selectedAttemptIndex) }}</h2>
          <p class="ns-panel__meta">
            {{ channelLabel(channelEditor) }}
            {{ channelEditorFocus === 'provider' ? 'provider' : (channelEditorFocus === 'messages' ? 'messages' : 'setup') }}
          </p>
        </div>
        <button type="button" class="ns-panel__close" (click)="closeChannelEditor()" aria-label="Close channel setup">×</button>
      </header>

      <div class="ns-panel__body ns-panel__body--form">
        <article class="ns-setup-card" *ngIf="channelEditorFocus !== 'messages'">
          <div class="ns-setup-card__head">
            <h3><span *ngIf="channelEditorFocus === 'all'">1</span> {{ channelLabel(channelEditor) }} Provider</h3>
          </div>
          <p class="ns-provider-note">
            Note: Selected Channel providers will be applied for all Languages and Attempts
          </p>

          <div class="ns-identity ns-identity--sms" *ngIf="channelEditor === 'sms'">
            <div class="ns-identity__row ns-identity__row--3">
              <label [class.is-dirty]="isSharedDirty('sms', 'sender_id')">
                Sender ID
                <input
                  type="text"
                  [disabled]="!canUpdate"
                  [ngModel]="config.channels.sms.sender_id"
                  (ngModelChange)="setBoundedText('smsSender', $event)"
                  [maxlength]="fieldMax.smsSender"
                  placeholder="BRANDNM"
                />
              </label>
              <label [class.is-dirty]="isSharedDirty('sms', 'provider')">
                SMS Provider (India)
                <select [(ngModel)]="config.channels.sms.provider" [disabled]="!canUpdate">
                  <option value="">Select SMS provider</option>
                  <option *ngFor="let option of providers.sms.domestic" [value]="option.id" [disabled]="option.is_available === false">
                    {{ option.name }}{{ option.is_available === false ? ' (unavailable)' : '' }}
                  </option>
                </select>
              </label>
              <label [class.is-dirty]="isSharedDirty('sms', 'international_provider')">
                SMS International Provider
                <select [(ngModel)]="config.channels.sms.international_provider" [disabled]="!canUpdate">
                  <option value="">Select SMS international provider</option>
                  <option *ngFor="let option of providers.sms.international" [value]="option.id" [disabled]="option.is_available === false">
                    {{ option.name }}{{ option.is_available === false ? ' (unavailable)' : '' }}
                  </option>
                </select>
              </label>
            </div>
            <p class="ns-warn" *ngIf="providerUnavailable(findProvider(providers.sms.domestic, config.channels.sms.provider))">
              This India SMS provider is marked unavailable.
            </p>
            <div class="ns-sms-trim">
              <label [class.is-dirty]="isSharedDirty('sms', 'sms_trim_enabled')">
                SMS Trim
                <span class="ns-fit ns-fit--check">
                  <input
                    type="checkbox"
                    [disabled]="!canUpdate"
                    [ngModel]="config.channels.sms.sms_trim_enabled"
                    (ngModelChange)="setSmsTrimEnabled($event)"
                  />
                  {{ config.channels.sms.sms_trim_enabled ? 'On' : 'Off' }}
                </span>
              </label>
              <label [class.is-muted]="!config.channels.sms.sms_trim_enabled" [class.is-dirty]="isSharedDirty('sms', 'recipient_name_length')">
                Recipient name max. length
                <input
                  type="number"
                  [min]="smsTrimLengthMin"
                  [max]="smsTrimLengthMax"
                  step="1"
                  placeholder="8"
                  [ngModel]="config.channels.sms.recipient_name_length"
                  (ngModelChange)="onSmsRecipientLengthChange($event)"
                  (keydown)="blockNonIntegerKeys($event)"
                  [disabled]="!canUpdate || !config.channels.sms.sms_trim_enabled"
                />
              </label>
              <label [class.is-muted]="!config.channels.sms.sms_trim_enabled" [class.is-dirty]="isSharedDirty('sms', 'default_recipient_name')">
                Default recipient name
                <input
                  type="text"
                  placeholder="Customer"
                  [(ngModel)]="config.channels.sms.default_recipient_name"
                  [attr.maxlength]="config.channels.sms.recipient_name_length || null"
                  [disabled]="!canUpdate || !config.channels.sms.sms_trim_enabled || !config.channels.sms.recipient_name_length"
                />
              </label>
            </div>
            <p class="ns-help ns-help--span">
              If a customer’s name is longer than the max length, it is replaced by the default recipient name (for example Customer).
            </p>
            <p class="ns-warn" *ngIf="config.channels.sms.sms_trim_enabled && !smsTrimLengthValid">
              Enter a length between 1 and 100, for example 8.
            </p>
            <p class="ns-warn" *ngIf="config.channels.sms.sms_trim_enabled && !smsTrimNameValid">
              Enter a default name no longer than the max length, for example Customer.
            </p>
          </div>

          <div class="ns-identity ns-identity--stack" *ngIf="channelEditor === 'email'">
            <div class="ns-identity__row">
              <label [class.is-dirty]="isSharedDirty('email', 'provider')">
                Email Provider
                <select [(ngModel)]="config.channels.email.provider" [disabled]="!canUpdate">
                  <option value="">Select email provider</option>
                  <option *ngFor="let option of providers.email" [value]="option.id" [disabled]="option.is_available === false">
                    {{ option.name }}{{ option.is_available === false ? ' (unavailable)' : '' }}
                  </option>
                </select>
              </label>
              <label [class.is-dirty]="isSharedDirty('email', 'reply_to')">
                Reply-to (optional)
                <input
                  type="email"
                  [disabled]="!canUpdate"
                  [ngModel]="config.channels.email.reply_to"
                  (ngModelChange)="setBoundedText('emailReply', $event)"
                  [maxlength]="fieldMax.emailReply"
                  placeholder="support@yourbrand.com"
                />
              </label>
            </div>
            <p class="ns-warn" *ngIf="providerUnavailable(findProvider(providers.email, config.channels.email.provider))">
              This email provider is marked unavailable.
            </p>
            <p class="ns-warn" *ngIf="config.channels.email.reply_to && !replyToLooksValid">
              Enter a valid reply-to email address.
            </p>
            <label [class.is-dirty]="isSharedDirty('email', 'from')">
              From
              <input
                type="text"
                class="ns-from-input"
                [disabled]="!canUpdate"
                [ngModel]="config.channels.email.from"
                (ngModelChange)="setBoundedText('emailFrom', $event)"
                [maxlength]="fieldMax.emailFrom"
                placeholder="Your Brand &lt;feedback&#64;yourbrand.com&gt;"
              />
              <small class="ns-help">Enter the sender like this: LitmusWorld &lt;feedback&#64;litmusworld.net&gt;.</small>
            </label>
            <p class="ns-warn" *ngIf="config.channels.email.from && !emailFromLooksValid">
              Enter the from address in this format: Your Brand &lt;feedback&#64;yourbrand.com&gt;.
            </p>
          </div>

          <div class="ns-identity ns-identity--stack" *ngIf="channelEditor === 'whatsapp'">
            <label [class.is-dirty]="isSharedDirty('whatsapp', 'sender_id')">
              Sender ID
              <input
                type="text"
                [disabled]="!canUpdate"
                [ngModel]="config.channels.whatsapp.sender_id"
                (ngModelChange)="setBoundedText('whatsappSender', $event)"
                [maxlength]="fieldMax.whatsappSender"
                placeholder="e.g. 9999900000"
              />
            </label>
            <label [class.is-dirty]="isSharedDirty('whatsapp', 'domestic_provider')">
              WhatsApp Provider (India)
              <select [(ngModel)]="config.channels.whatsapp.domestic_provider" [disabled]="!canUpdate">
                <option value="">Select WhatsApp provider (India)</option>
                <option *ngFor="let option of providers.whatsapp.domestic" [value]="option.id" [disabled]="option.is_available === false">
                  {{ option.name }}{{ option.is_available === false ? ' (unavailable)' : '' }}
                </option>
              </select>
            </label>
            <label [class.is-dirty]="isSharedDirty('whatsapp', 'international_provider')">
              WhatsApp International Provider
              <select [(ngModel)]="config.channels.whatsapp.international_provider" [disabled]="!canUpdate">
                <option value="">Select WhatsApp international provider</option>
                <option *ngFor="let option of providers.whatsapp.international" [value]="option.id" [disabled]="option.is_available === false">
                  {{ option.name }}{{ option.is_available === false ? ' (unavailable)' : '' }}
                </option>
              </select>
            </label>
            <p class="ns-warn" *ngIf="providerUnavailable(findProvider(providers.whatsapp.domestic, config.channels.whatsapp.domestic_provider))">
              This India WhatsApp provider is marked unavailable.
            </p>
            <p class="ns-warn" *ngIf="providerUnavailable(findProvider(providers.whatsapp.international, config.channels.whatsapp.international_provider))">
              This international WhatsApp provider is marked unavailable.
            </p>
          </div>
        </article>

        <article class="ns-setup-card" *ngIf="channelEditorFocus !== 'provider'">
          <div class="ns-setup-card__head">
            <h3><span *ngIf="channelEditorFocus === 'all'">2</span> Language</h3>
            <button type="button" class="ns-save ns-save--sm" (click)="openPreview()">Preview as customer</button>
          </div>
          <p class="ns-help">
            Click a language to edit its template. Customers receive the default language unless they match another language.
          </p>
          <div class="ns-langs ns-langs--toolbar">
            <div class="ns-langs__chips">
              <span
                class="ns-lang-item"
                *ngFor="let lang of languages"
                [class.is-active]="currentLanguage === lang"
                [class.is-default]="isDefaultLanguage(lang)"
              >
                <button
                  type="button"
                  class="ns-lang"
                  [title]="languageLabel(lang)"
                  (click)="selectLanguage(lang)"
                >
                  {{ languageShort(lang) }}
                  <em *ngIf="isDefaultLanguage(lang)">Default</em>
                </button>
                <button
                  type="button"
                  class="ns-lang-remove"
                  *ngIf="canUpdate"
                  [attr.aria-label]="'Remove ' + languageLabel(lang)"
                  [title]="'Remove ' + languageLabel(lang)"
                  (click)="askRemoveLanguage(lang)"
                >
                  <i class="fa fa-trash" aria-hidden="true"></i>
                </button>
              </span>
            </div>
            <div class="ns-add-lang" *ngIf="availableLanguages.length">
              <select
                [(ngModel)]="newLanguage"
                [disabled]="!canUpdate || !!languageAddBlockedMessage()"
                [title]="languageAddBlockedMessage()"
                aria-label="Add a language"
              >
                <option value="">Add a language</option>
                <option *ngFor="let option of availableLanguages" [value]="option.key">
                  {{ option.name }} ({{ option.native_name }})
                </option>
              </select>
              <button
                type="button"
                class="ns-save ns-save--sm"
                [class.is-unauthorized]="!canUpdate"
                [disabled]="!canUpdate || !!languageAddBlockedMessage()"
                [title]="languageAddBlockedMessage()"
                (click)="addLanguage()"
              >
                Add
              </button>
            </div>
          </div>
          <p class="ns-warn" *ngIf="languageAddBlockedMessage() as blocked">{{ blocked }}</p>
        </article>

        <article class="ns-setup-card" *ngIf="channelEditorFocus !== 'provider'">
          <div class="ns-setup-card__head">
            <h3><span *ngIf="channelEditorFocus === 'all'">3</span> Message</h3>
            <em *ngIf="currentLanguage">{{ languageLabel(currentLanguage) }}</em>
          </div>
          <p class="ns-help" *ngIf="!currentLanguage">Choose a language above, then add the template details.</p>
          <p class="ns-default-note" *ngIf="currentLanguage && isDefaultLanguage(currentLanguage)">
            <strong>{{ languageLabel(currentLanguage) }}</strong> is the default language.
          </p>
          <label class="ns-default-check" *ngIf="currentLanguage && !isDefaultLanguage(currentLanguage)">
            <input
              type="checkbox"
              [disabled]="!canUpdate"
              (change)="setDefaultLanguage(currentLanguage)"
            />
            Make {{ languageLabel(currentLanguage) }} the default language
          </label>

          <ng-container *ngIf="currentLanguage && channelEditor === 'sms'">
            <label [class.is-dirty]="isTemplateDirty('content_template_id')">
              {{ isOnDemand ? 'SMS template' : 'Content Template Id' }}
              <select
                name="nsSmsTemplate"
                [ngModel]="smsTemplateSelectId"
                (ngModelChange)="onSmsDltTemplateChange($event)"
                [compareWith]="compareSelectIds"
                [disabled]="!canUpdate || !hasTemplatesForLanguage('sms')"
                (change)="$event.stopPropagation()"
              >
                <option [ngValue]="''">{{ hasTemplatesForLanguage('sms') ? (isOnDemand ? 'Please select an SMS template' : 'Please Select Dlt Template Id') : 'No SMS template found for ' + languageLabel(currentLanguage) }}</option>
                <option *ngFor="let option of smsDltOptions; trackBy: trackByDltOption" [ngValue]="option.rowId || option.id">
                  {{ option.id }}{{ option.name ? ' (' + option.name + ')' : '' }}
                </option>
              </select>
            </label>
            <p class="ns-help" *ngIf="!isOnDemand">Content Template Id (Data Ledger Technology).</p>
            <p class="ns-warn" *ngIf="isOnDemand && hasTemplatesForLanguage('sms') && !currentTemplate.content_template_id?.trim()">
              Select an SMS template for this language before you can save this attempt.
            </p>
            <p class="ns-help" *ngIf="isOnDemand && hasTemplatesForLanguage('sms')">Templates come from this project's SMS template list. Create them on the Templates tab.</p>
            <label>
              {{ isOnDemand ? 'SMS template text' : 'Content Template Id Text' }}
              <textarea
                rows="4"
                class="ns-dlt-text"
                [ngModel]="currentTemplate.content_template_text"
                [placeholder]="isOnDemand ? 'Select a template to see its text' : 'to enable dlt please select template id'"
                disabled
              ></textarea>
            </label>
            <ng-container *ngIf="!isOnDemand">
            <label [class.is-dirty]="isTemplateDirty('sms_body')">
              SMS message
              <textarea
                rows="8"
                #smsBodyInput
                [maxlength]="fieldMax.smsBody"
                [ngModel]="currentTemplate.sms_body"
                (ngModelChange)="setTemplateText('sms_body', $event)"
                (select)="rememberCaret('sms_body', $event)"
                (keyup)="rememberCaret('sms_body', $event)"
                (mouseup)="rememberCaret('sms_body', $event)"
                (blur)="rememberCaret('sms_body', $event)"
                placeholder="Hi --customerName--, please share feedback: --feedbackUrl--"
                [disabled]="!canUpdate"
              ></textarea>
            </label>
            <p class="ns-warn" *ngIf="!currentTemplate.sms_body?.trim()">
              Write the SMS message for this language before you can save this attempt.
            </p>
            <div class="ns-sms-meter" *ngIf="currentTemplate.sms_body">
              <p class="ns-sms-meter__hint" *ngIf="smsMissingUrlHint">
                It is recommended to add Unsubscribe link. If the SMS is being set for a conversation request, please add a Feedback URL.
              </p>
              <dl class="ns-sms-stats">
                <div><dt>Language</dt><dd>{{ languageLabel(currentLanguage) }}</dd></div>
                <div><dt>Encoding</dt><dd>{{ smsEncoding }}{{ smsEncoding === 'unicode' ? ' (70/67)' : ' (160/153)' }}</dd></div>
                <div><dt>SMS parts</dt><dd>{{ smsSegmentCount }}</dd></div>
                <div><dt>Chars used</dt><dd [class.is-over]="smsOverGsmLimit">{{ smsCountedLength }}</dd></div>
                <div><dt>Chars in SMS</dt><dd>{{ smsCharLimit }}</dd></div>
              </dl>
              <p class="ns-sms-meter__hint" *ngIf="smsHasFeedbackUrlToken">
                {{ smsLinkHint }}
              </p>
            </div>
            </ng-container>
          </ng-container>

          <ng-container *ngIf="currentLanguage && channelEditor === 'email' && isOnDemand">
            <label [class.is-dirty]="isTemplateDirty('email_template_id')">
              Email template
              <select
                name="nsEmailTemplate"
                [ngModel]="currentTemplate.email_template_id || ''"
                (ngModelChange)="onEmailCatalogChange($event)"
                [compareWith]="compareSelectIds"
                [disabled]="!canUpdate || !hasTemplatesForLanguage('email')"
                (change)="$event.stopPropagation()"
              >
                <option [ngValue]="''">{{ hasTemplatesForLanguage('email') ? 'Please select an email template' : 'No email template found for ' + languageLabel(currentLanguage) }}</option>
                <option *ngFor="let option of emailCatalogOptions; trackBy: trackByCatalogOption" [ngValue]="option.id">
                  {{ option.template_title || option.template_name }}{{ option.email_subject ? ' — ' + option.email_subject : '' }}
                </option>
              </select>
            </label>
            <p class="ns-warn" *ngIf="hasTemplatesForLanguage('email') && !currentTemplate.email_template_id?.trim() && !currentTemplate.template_name?.trim() && !currentTemplate.template_file_id?.trim()">
              Select an email template for this language before you can save this attempt.
            </p>
            <p class="ns-help" *ngIf="hasTemplatesForLanguage('email')">Templates come from this project's email template list. Create them on the Templates tab.</p>
            <label [class.is-dirty]="isTemplateDirty('email_subject')">
              Email subject
              <input type="text" [ngModel]="currentTemplate.email_subject" disabled />
            </label>
            <div class="ns-upload">
              <div class="ns-upload__copy">
                <strong>Selected template</strong>
                <p class="ns-upload__file">{{ currentTemplate.template_name || 'None selected' }}</p>
              </div>
              <div class="ns-upload__actions">
                <button
                  type="button"
                  class="ns-save ns-save--sm ns-save--ghost"
                  *ngIf="hasEmailTemplate"
                  (click)="viewEmailTemplate()"
                >
                  View
                </button>
                <button
                  type="button"
                  class="ns-save ns-save--sm ns-save--ghost"
                  *ngIf="hasEmailTemplate"
                  (click)="previewEmailTemplate()"
                >
                  Preview
                </button>
                <button
                  type="button"
                  class="ns-save ns-save--sm ns-save--ghost"
                  *ngIf="hasEmailTemplate"
                  (click)="downloadEmailTemplate()"
                >
                  Download
                </button>
              </div>
            </div>
          </ng-container>

          <ng-container *ngIf="currentLanguage && channelEditor === 'email' && !isOnDemand">
            <label [class.is-dirty]="isTemplateDirty('email_subject')">
              Email subject
              <input
                type="text"
                #emailSubjectInput
                [maxlength]="fieldMax.emailSubject"
                [ngModel]="currentTemplate.email_subject"
                (ngModelChange)="setTemplateText('email_subject', $event)"
                (select)="rememberCaret('email_subject', $event)"
                (keyup)="rememberCaret('email_subject', $event)"
                (mouseup)="rememberCaret('email_subject', $event)"
                (blur)="rememberCaret('email_subject', $event)"
                placeholder="We value your feedback"
                [disabled]="!canUpdate"
                required
              />
            </label>
            <p class="ns-warn" *ngIf="!currentTemplate.email_subject?.trim()">
              Email subject is required before you can save this attempt.
            </p>
            <p class="ns-warn" *ngIf="!hasEmailTemplateFor(currentTemplate)">
              Upload an HTML template for this language before you can save this attempt.
            </p>
            <div class="ns-upload">
              <div class="ns-upload__copy">
                <strong>HTML template</strong>
                <p>Upload a .html file. Placeholders such as {{ '{{customerName}}' }} and {{ '{{feedbackUrl}}' }} are replaced when we send.</p>
                <p class="ns-upload__file" *ngIf="currentTemplate.template_file_name || currentTemplate.template_file_id">
                  {{ currentTemplate.template_file_name || 'Template uploaded' }}
                </p>
              </div>
              <div class="ns-upload__actions">
                <input
                  #emailHtmlFileInput
                  class="ns-upload__file-input"
                  type="file"
                  accept=".html,text/html"
                  tabindex="-1"
                  aria-hidden="true"
                  (change)="onEmailTemplateSelected($event)"
                  [disabled]="!canUpdate || uploadingEmail"
                />
                <button
                  type="button"
                  class="ns-save ns-save--sm"
                  [class.is-unauthorized]="!canUpdate"
                  [disabled]="canUpdate && uploadingEmail"
                  (click)="emailHtmlFileInput.click()"
                >
                  {{ uploadingEmail ? 'Uploading…' : (currentTemplate.template_file_id ? 'Replace file' : 'Upload HTML') }}
                </button>
                <button
                  type="button"
                  class="ns-save ns-save--sm ns-save--ghost"
                  *ngIf="hasEmailTemplate"
                  (click)="viewEmailTemplate()"
                >
                  View
                </button>
                <button
                  type="button"
                  class="ns-save ns-save--sm ns-save--ghost"
                  *ngIf="hasEmailTemplate"
                  (click)="previewEmailTemplate()"
                >
                  Preview
                </button>
                <button
                  type="button"
                  class="ns-save ns-save--sm ns-save--ghost"
                  *ngIf="hasEmailTemplate"
                  (click)="downloadEmailTemplate()"
                >
                  Download
                </button>
              </div>
            </div>
            <ng-container *ngIf="showEmailHtmlEditor">
              <label [class.is-dirty]="isTemplateDirty('email_body')">
                Edit HTML
                <textarea
                  #emailBodyInput
                  class="ns-html-editor"
                  rows="16"
                  [maxlength]="fieldMax.emailHtml"
                  [ngModel]="currentTemplate.email_body"
                  (ngModelChange)="setTemplateText('email_body', $event)"
                  (select)="rememberCaret('email_body', $event)"
                  (keyup)="rememberCaret('email_body', $event)"
                  (mouseup)="rememberCaret('email_body', $event)"
                  (blur)="rememberCaret('email_body', $event)"
                  [placeholder]="emailHtmlPlaceholder"
                  [disabled]="!canUpdate || loadingEmailHtml"
                ></textarea>
              </label>
              <div class="ns-tokens ns-tokens--after-editor">
                <span
                  *ngFor="let field of mergeFields"
                  [class.is-used]="isDynamicTokenUsed(field)"
                  [class.is-unauthorized]="!canUpdate"
                  (mousedown)="$event.preventDefault()"
                  (click)="insertDynamicToken(field)"
                >
                  {{ field.label }}
                  <code>{{ tokenForMessageTab(field) }}</code>
                </span>
              </div>
              <p class="ns-tokens-hint" *ngIf="usesDynamicText">{{ dynamicTextHint }}</p>
              <button
                type="button"
                class="ns-save ns-save--sm"
                (click)="saveEmailHtml()"
                [class.is-unauthorized]="!canUpdate"
                [disabled]="canUpdate && !canSaveEmailHtml"
              >
                Save HTML
              </button>
            </ng-container>
          </ng-container>

          <ng-container *ngIf="currentLanguage && channelEditor === 'whatsapp'">
            <p class="ns-help" *ngIf="isOnDemand && hasTemplatesForLanguage('whatsapp')">Templates come from this project's WhatsApp template list. Create them on the Templates tab.</p>
            <label [class.is-dirty]="isTemplateDirty('whatsapp_template_id')">
              WhatsApp Template Id
              <select
                name="nsWhatsappTemplate"
                [ngModel]="whatsappTemplateSelectId"
                (ngModelChange)="onWhatsappTemplateChange($event)"
                [compareWith]="compareSelectIds"
                [disabled]="!canUpdate || !hasTemplatesForLanguage('whatsapp')"
                (change)="$event.stopPropagation()"
              >
                <option [ngValue]="''">{{ hasTemplatesForLanguage('whatsapp') ? 'Please Select WhatsApp Template Id' : 'No WhatsApp template found for ' + languageLabel(currentLanguage) }}</option>
                <option *ngFor="let option of whatsappTemplateOptions; trackBy: trackByDltOption" [ngValue]="option.rowId || option.id">
                  {{ option.id }}{{ option.name ? ' (' + option.name + ')' : '' }}
                </option>
              </select>
            </label>
            <p class="ns-warn" *ngIf="hasTemplatesForLanguage('whatsapp') && !currentTemplate.whatsapp_template_id?.trim()">
              Select a WhatsApp Template Id for this language before you can save this attempt.
            </p>
            <p class="ns-warn" *ngIf="!isOnDemand && currentTemplate.whatsapp_template_id?.trim() && !currentTemplate.whatsapp_body?.trim()">
              Select a WhatsApp template that has template text before you can save this attempt.
            </p>
            <label>
              WhatsApp Template Id Text
              <textarea
                rows="6"
                class="ns-dlt-text"
                [ngModel]="currentTemplate.whatsapp_body"
                placeholder="Will display WhatsApp text message of the selected WhatsApp template id"
                disabled
              ></textarea>
            </label>
          </ng-container>

          <div class="ns-tokens ns-tokens--after-editor" *ngIf="currentLanguage && channelEditor !== 'email' && channelEditor !== 'whatsapp' && !isOnDemand">
            <span
              *ngFor="let field of mergeFields"
              [class.is-used]="isDynamicTokenUsed(field)"
              [class.is-unauthorized]="!canUpdate"
              (mousedown)="$event.preventDefault()"
              (click)="insertDynamicToken(field)"
            >
              {{ field.label }}
              <code>{{ tokenForMessageTab(field) }}</code>
            </span>
          </div>
          <p class="ns-tokens-hint ns-tokens-hint--after-editor" *ngIf="currentLanguage && channelEditor !== 'email' && channelEditor !== 'whatsapp' && !isOnDemand && usesDynamicText">{{ dynamicTextHint }}</p>
        </article>
      </div>

      <footer class="ns-panel__foot">
        <button
          type="button"
          class="ns-save ns-save--ghost"
          [disabled]="!canDiscardAttempt"
          [title]="canDiscardAttempt ? 'Discard unsaved changes to ' + attemptDisplayName(selectedAttemptIndex) : ''"
          (click)="discardAttemptChanges()"
        >
          Discard changes
        </button>
        <button type="button" class="ns-save ns-save--ghost ns-panel__foot-end" (click)="finishOrCloseEditor()">{{ canUpdate ? 'Done' : 'Close' }}</button>
        <button
          type="button"
          class="ns-save"
          [class.is-unauthorized]="!canUpdate"
          [disabled]="canUpdate && !canSaveAttempt"
          [title]="smsTemplateGap(selectedAttempt) || emailSubjectGap(selectedAttempt) || whatsappTemplateGap(selectedAttempt) || delayMinutesGap(selectedAttempt) || ''"
          (click)="saveFromChannelEditor()"
        >
          {{ saving ? 'Saving…' : 'Save attempt' }}
        </button>
      </footer>
    </aside>
  </div>

  <div class="ns-drawer ns-drawer--preview" *ngIf="previewOpen">
    <div class="ns-drawer__overlay" (click)="closePreview()"></div>
    <aside
      class="ns-drawer__panel ns-drawer__panel--preview"
      [class.ns-drawer__panel--mail]="previewChannel === 'email'"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ns-preview-title"
    >
      <header class="ns-panel__head">
        <div>
          <h2 id="ns-preview-title">{{ channelLabel(previewChannel) }} preview</h2>
          <p>{{ languageLabel(currentLanguage) }} · sample customer, not a live send</p>
        </div>
        <button type="button" class="ns-panel__close" (click)="closePreview()" aria-label="Close preview">×</button>
      </header>

      <div class="ns-preview-nav">
        <div class="ns-device-tabs" role="tablist" aria-label="Channel" *ngIf="previewChannelTabs.length > 1">
          <button
            type="button"
            *ngFor="let tab of previewChannelTabs"
            [class.is-active]="previewChannel === tab"
            (click)="setPreviewChannel(tab)"
          >
            {{ channelLabel(tab) }}
          </button>
        </div>
        <div class="ns-device-tabs" role="tablist" aria-label="Device" *ngIf="previewChannel !== 'email'">
          <button
            type="button"
            *ngFor="let device of previewDevices"
            [class.is-active]="previewDevice === device.id"
            (click)="setPreviewDevice(device.id)"
          >
            {{ device.label }}
          </button>
        </div>
      </div>

      <div class="ns-panel__body ns-panel__body--preview">
        <p class="ns-preview__legend">
          Sample links only. Live messages use each customer’s data.
        </p>

        <div class="ns-preview-stage" *ngIf="previewChannel === 'sms'">
          <div class="ns-device-frame" [class.is-wide]="previewDevice !== 'mobile'">
            <div class="ns-sms">
              <div class="ns-sms__bar">
                <span class="ns-sms__carrier">{{ previewClock }}</span>
                <strong>{{ config.channels.sms.sender_id || 'Your brand' }}</strong>
                <span>Text Message</span>
              </div>
              <div class="ns-sms__thread">
                <p class="ns-sms__stamp">Today · {{ previewClock }}</p>
                <div class="ns-bubble ns-bubble--sms" *ngIf="smsPreviewText" [innerHTML]="formatTextPreview(currentTemplate.sms_body)"></div>
                <p class="ns-preview__empty" *ngIf="!smsPreviewText">Write an SMS in channel setup to preview it here.</p>
              </div>
            </div>
          </div>
          <aside class="ns-preview-facts" *ngIf="smsPreviewText">
            <h3>What the customer sees</h3>
            <dl>
              <div><dt>Sender</dt><dd>{{ config.channels.sms.sender_id || 'Sender ID not set' }}</dd></div>
              <div><dt>Language</dt><dd>{{ languageLabel(currentLanguage) }}</dd></div>
              <div><dt>Encoding</dt><dd>{{ smsEncoding }}{{ smsEncoding === 'unicode' ? ' (70/67)' : ' (160/153)' }}</dd></div>
              <div><dt>SMS parts</dt><dd>{{ smsSegmentCount }}</dd></div>
              <div><dt>Chars used</dt><dd>{{ smsCountedLength }}</dd></div>
              <div><dt>Chars in SMS</dt><dd>{{ smsCharLimit }}</dd></div>
            </dl>
          </aside>
        </div>

        <div class="ns-preview-stage" *ngIf="previewChannel === 'whatsapp'">
          <div class="ns-device-frame ns-device-frame--wa" [class.is-wide]="previewDevice !== 'mobile'">
            <div class="ns-wa">
              <div class="ns-wa__bar">
                <span class="ns-wa__avatar">{{ previewWhatsAppInitials }}</span>
                <div>
                  <strong>{{ config.channels.whatsapp.sender_id || 'WhatsApp business' }}</strong>
                  <span>Business account</span>
                </div>
              </div>
              <div class="ns-wa__thread">
                <p class="ns-sms__stamp">Today</p>
                <div class="ns-bubble ns-bubble--wa" *ngIf="whatsappPreviewText" [innerHTML]="formatTextPreview(currentTemplate.whatsapp_body)"></div>
                <p class="ns-preview__empty" *ngIf="!whatsappPreviewText">Select a WhatsApp Template Id in channel setup to preview it here.</p>
                <p class="ns-wa__ticks" *ngIf="whatsappPreviewText">{{ previewClock }} ✓✓</p>
              </div>
            </div>
          </div>
          <aside class="ns-preview-facts">
            <h3>What the customer sees</h3>
            <p>A chat from your WhatsApp sender. Live delivery still uses the approved template on the provider.</p>
          </aside>
        </div>

        <div class="ns-preview-stage ns-preview-stage--mail" *ngIf="previewChannel === 'email'">
          <div class="ns-inbox">
            <div class="ns-inbox__head">
              <p class="ns-inbox__subject">{{ emailPreviewSubject || 'Add a subject to preview it' }}</p>
              <div class="ns-inbox__from">
                <span class="ns-wa__avatar">{{ previewFromInitials }}</span>
                <div>
                  <strong>{{ emailFromDisplayName }}</strong>
                </div>
              </div>
            </div>
            <div class="ns-inbox__body">
              <iframe
                *ngIf="emailPreviewUrl"
                class="ns-mail__frame"
                [src]="emailPreviewUrl"
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                title="Email preview"
              ></iframe>
              <p class="ns-preview__empty" *ngIf="!emailPreviewUrl && (currentTemplate.email_template_id || currentTemplate.template_file_id)">
                Loading HTML preview…
              </p>
              <p class="ns-preview__empty" *ngIf="!emailPreviewUrl && !currentTemplate.email_template_id && !currentTemplate.template_file_id">
                Upload HTML in Email setup, then preview again.
              </p>
            </div>
          </div>
        </div>
      </div>
    </aside>
  </div>

  <div class="ns-confirm" *ngIf="confirmBox" [class.has-extra]="!!confirmBox.extraAction">
    <div class="modal modal-dialog-centered" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-body confirmation-body">
            <div>
              <span class="alert-header" [class.is-ok]="confirmBox.tone === 'ok'">ALERT</span>
            </div>
            <p>{{ confirmBox.message }}</p>
            <div class="ns-confirm__actions">
              <button type="button" class="btn btn-cancel btn-custom-cancel" (click)="closeConfirm(false)">
                Don't continue
              </button>
              <button
                type="button"
                class="btn btn-default btn-custom-apply alert-ltms-btn"
                [class.is-ok]="confirmBox.tone === 'ok'"
                (click)="closeConfirm(true)"
              >
                Yes, continue
              </button>
              <button
                type="button"
                class="btn btn-default btn-custom-apply alert-ltms-btn is-extra"
                *ngIf="confirmBox.extraAction"
                (click)="closeConfirmExtra()"
              >
                {{ confirmBox.extraAction.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
